import { notification } from 'antd'
import { ANNOTATION_CATEGORIES } from 'constants/annotation'
import Api from 'services/api'
import { I18nT } from 'types'
import Krpano from 'utils/krpano'
import { KrpanoEngine } from 'utils/KrpanoEngine'
import {
  getPointVersion,
  getPointVersionFromResponse,
  handlePointVersionError,
  syncPointVersion
} from 'utils/pointVersion'

import { CommentEntity } from '../CommentEntity'
import { decodeHtmlEntities } from '../mappers'
import { CommentItem, CommentRecipient } from '../types'

import { CommentFormService } from './CommentFormService'

export interface ResolvedCommentTargetPosition {
  annotationId: string
  targetAnno: any | null
  h?: number
  v?: number
}

export interface CommentWorkflowDependencies {
  saveCommentWithAnnotation: (params: any) => Promise<any>
  addReply: (commentId: number | string, payload: any) => Promise<any>
  deleteComment: (commentId: number | string) => Promise<any>
  deleteAnnotation?: (payload: any) => Promise<any>
  mutateComments: () => Promise<any>
  setCurrentSelected: (val: any) => void
  setPointDetail: (fn: any) => void
  setPoints?: (fn: any) => void
  t: I18nT
  userList?: any[]
  userGroups?: any[]
  currentUser?: { id?: number | string; role?: string } | null
}

export class CommentDomainService {
  constructor(private deps: CommentWorkflowDependencies) {}

  /**
   * Merges System Admin (SA) users from userList into a CommentItem's recipients
   */
  static mergeAdminRecipientsToComment(
    comment: CommentItem,
    userList?: any[]
  ): CommentItem {
    if (!comment || !Array.isArray(userList) || userList.length === 0) {
      return comment
    }
    const adminUsers = userList.filter((u) =>
      CommentFormService.isAdminRecipientRole(u)
    )
    if (adminUsers.length === 0) return comment

    const existingIds = new Set(
      (comment.recipients || [])
        .map((r: any) =>
          typeof r === 'number'
            ? r
            : Number(r?.recipientId || r?.recipient_id || r?.id || r)
        )
        .filter((id) => !Number.isNaN(id) && id > 0)
    )

    const missingAdminRecipients: CommentRecipient[] = []
    adminUsers.forEach((au: any) => {
      const auId = Number(au.id || au.user_id)
      if (auId > 0 && !existingIds.has(auId)) {
        missingAdminRecipients.push({
          targetType: 'comment',
          targetId: comment.id,
          annotationId: comment.annotationId,
          recipientType: 'user',
          recipientId: auId,
          name:
            `${au.lastName || au.last_name || ''} ${
              au.firstName || au.first_name || ''
            }`.trim() || au.name,
          email: au.email
        })
      }
    })

    if (missingAdminRecipients.length === 0) return comment

    return {
      ...comment,
      recipients: [...(comment.recipients || []), ...missingAdminRecipients]
    }
  }

  /**
   * Normalizes annotation & comment data into a unified CommentItem model
   */
  static normalizeCommentDetail(
    currentSelected: any,
    comments: CommentItem[],
    id?: string,
    floorId?: string,
    currentSelectedPoint?: string,
    userList?: any[]
  ): CommentItem | null {
    if (!currentSelected?.id) return null

    const gMap =
      typeof window !== 'undefined' ? (window as any).rnCommentsMap : null
    const getCached = (key?: any) => {
      if (!key || !gMap) return null
      const strKey = String(key)
      if (gMap instanceof Map) return gMap.get(strKey) || null
      return gMap[strKey] || null
    }

    const cachedComment =
      getCached(currentSelected.id) ||
      getCached(currentSelected.comment_id) ||
      getCached(currentSelected.annotation_id) ||
      getCached(currentSelected.annotationId) ||
      getCached(currentSelected.comment?.id)

    const foundInList =
      comments?.find(
        (c) =>
          String(c.annotationId) === String(currentSelected.id) ||
          String(c.id) === String(currentSelected.id) ||
          (currentSelected.comment_id &&
            String(c.id) === String(currentSelected.comment_id)) ||
          (currentSelected.comment?.id &&
            String(c.id) === String(currentSelected.comment.id))
      ) || cachedComment

    const relationalComment = currentSelected.comment || foundInList
    const snapshotComment =
      currentSelected.comment_object || currentSelected.commentObject

    const embeddedComment = relationalComment || snapshotComment
    if (embeddedComment) {
      const creatorName =
        relationalComment?.creator_name ||
        relationalComment?.creatorName ||
        relationalComment?.author?.name ||
        snapshotComment?.creator_name ||
        snapshotComment?.creatorName ||
        currentSelected.creator_name ||
        '投稿者'

      const item: CommentItem = {
        id: Number(embeddedComment.id || currentSelected.id),
        buildingId: Number(
          embeddedComment.building_id || embeddedComment.buildingId || id || 0
        ),
        floorId: Number(
          embeddedComment.floor_id || embeddedComment.floorId || floorId || 0
        ),
        pointId: String(
          embeddedComment.point_id ||
            embeddedComment.pointId ||
            currentSelectedPoint ||
            ''
        ),
        annotationId: String(
          embeddedComment.annotation_id ||
            embeddedComment.annotationId ||
            currentSelected.id
        ),
        pointOrder:
          embeddedComment.point_order || embeddedComment.pointOrder || 0,
        code: embeddedComment.code || String(currentSelected.id),
        title: decodeHtmlEntities(
          relationalComment?.title ||
            snapshotComment?.title ||
            currentSelected.name ||
            currentSelected.title ||
            'コメント'
        ),
        body:
          relationalComment?.body ||
          embeddedComment?.body ||
          currentSelected.body ||
          '',
        s3Path:
          relationalComment?.s3_path ||
          relationalComment?.s3Path ||
          embeddedComment?.s3_path ||
          embeddedComment?.s3Path ||
          currentSelected?.comment?.s3_path ||
          currentSelected?.comment?.s3Path ||
          currentSelected?.s3_path ||
          currentSelected?.s3Path ||
          '',
        objectCat:
          relationalComment?.object_cat ||
          snapshotComment?.object_cat ||
          currentSelected?.comment_object?.object_cat ||
          currentSelected?.comment?.object_cat ||
          currentSelected?.object_cat ||
          'A',
        scope: relationalComment?.scope || snapshotComment?.scope || 'common',
        status: relationalComment?.status || snapshotComment?.status || 'open',
        authorId:
          relationalComment?.author_id ||
          relationalComment?.authorId ||
          snapshotComment?.created_by ||
          0,
        author: relationalComment?.author || { name: creatorName },
        posH: Number(embeddedComment.pos_h || currentSelected.h || 0),
        posV: Number(embeddedComment.pos_v || currentSelected.v || 0),
        year:
          embeddedComment.year ||
          currentSelected?.year ||
          currentSelected?.comment_object?.year ||
          undefined,
        recipients: (
          relationalComment?.recipients ||
          snapshotComment?.recipients ||
          currentSelected.recipients ||
          []
        ).map((r: any) =>
          typeof r === 'number'
            ? { recipientId: r, recipient_id: r }
            : {
                recipientId: r.recipient_id || r.recipientId || r.id || r,
                recipient_id: r.recipient_id || r.recipientId || r.id || r,
                ...r
              }
        ),
        replies: [],
        createdAt:
          embeddedComment.created_at ||
          embeddedComment.createdAt ||
          currentSelected.created_at ||
          '',
        updatedAt:
          embeddedComment.updated_at ||
          embeddedComment.updatedAt ||
          currentSelected.updated_at ||
          '',
        creatorName
      }
      return CommentDomainService.mergeAdminRecipientsToComment(item, userList)
    }

    const found = comments.find(
      (c) =>
        String(c.annotationId) === String(currentSelected.id) ||
        String(c.id) === String(currentSelected.id)
    )
    if (found) {
      return CommentDomainService.mergeAdminRecipientsToComment(found, userList)
    }

    const fallbackItem: CommentItem = {
      id: Number(currentSelected.id),
      buildingId: Number(id || 0),
      floorId: Number(floorId || 0),
      pointId: String(currentSelectedPoint || currentSelected.point_id || ''),
      annotationId: String(currentSelected.id),
      pointOrder: currentSelected.point_order || 0,
      code: currentSelected.code || String(currentSelected.id),
      title: decodeHtmlEntities(
        currentSelected.name ||
          currentSelected.title ||
          currentSelected.set_annotation_name ||
          'コメント'
      ),
      body: currentSelected.body || '',
      objectCat:
        currentSelected?.comment_object?.object_cat ||
        currentSelected?.comment?.object_cat ||
        currentSelected?.object_cat ||
        'A',
      scope: currentSelected.scope || 'common',
      status: currentSelected.status || 'open',
      authorId: currentSelected.author_id || 0,
      author: currentSelected.author || {
        name: currentSelected.author_name || '投稿者'
      },
      posH: Number(currentSelected.h || 0),
      posV: Number(currentSelected.v || 0),
      year:
        currentSelected?.year ||
        currentSelected?.comment_object?.year ||
        undefined,
      recipients: currentSelected.recipients || [],
      replies: currentSelected.replies || [],
      createdAt: currentSelected.created_at || currentSelected.createdAt || '',
      updatedAt: currentSelected.updated_at || currentSelected.updatedAt || ''
    }

    return CommentDomainService.mergeAdminRecipientsToComment(
      fallbackItem,
      userList
    )
  }

  /**
   * Formats location header text for the floor & point
   */
  static formatFloorPointText(
    currentSelected: any,
    floorDetail: any,
    pointDetail: any,
    points: any[],
    currentSelectedPoint?: string
  ): string {
    const selectedAny = currentSelected as any
    const currentFloorName =
      selectedAny?.floor_name || floorDetail?.name || floorDetail?.title || ''

    const targetPointInList = points?.find(
      (pt: any) =>
        String(pt.id) === String(currentSelectedPoint || selectedAny?.point_id)
    )

    const pointOrderVal =
      selectedAny?.point_order || pointDetail?.order || targetPointInList?.order

    const currentPointName =
      selectedAny?.point_name ||
      (pointOrderVal ? `ポイント ${pointOrderVal}` : '') ||
      pointDetail?.name ||
      targetPointInList?.areaName ||
      ''

    return [currentFloorName, currentPointName].filter(Boolean).join(' ・ ')
  }

  /**
   * Executes save comment workflow & Krpano hotspot synchronization
   */
  async handleSave(
    currentSelected: any,
    currentSelectedPoint: string | undefined,
    buildingId: number | string,
    floorId: number | string,
    payload: any,
    pointDetail?: any,
    points?: any[]
  ): Promise<void> {
    if (!currentSelected) return

    const effectiveFloorId =
      Number(floorId) ||
      Number(currentSelected?.floor_id) ||
      Number(currentSelected?.floorId) ||
      0
    const effectivePointId =
      currentSelectedPoint ||
      currentSelected?.point_id ||
      currentSelected?.pointId ||
      ''

    const res = await this.deps.saveCommentWithAnnotation({
      buildingId: Number(buildingId),
      floorId: effectiveFloorId,
      pointId: effectivePointId,
      currentSelected,
      payload,
      pointDetail,
      points
    })

    const newVersion =
      res.pointVersionId ?? getPointVersionFromResponse(res.annotation)

    if (newVersion != null && this.deps.setPoints) {
      syncPointVersion(
        newVersion,
        effectivePointId,
        this.deps.setPointDetail,
        this.deps.setPoints
      )
    }

    if (res.isNew && res.annotation) {
      const krpano = (document.getElementById('embedpano-full') ||
        document.getElementById('embedpano-viewer')) as any
      if (krpano) {
        krpano.call?.('hide_temp_hotspot()')
        Krpano.AddAnnotation(
          krpano,
          [res.annotation],
          null,
          false,
          true,
          'rn',
          this.deps.userList,
          this.deps.currentUser
        )
      }

      this.deps.setPointDetail(
        (item: any) =>
          item && {
            ...item,
            ...(newVersion != null ? { pointVersionId: newVersion } : {}),
            annotations: [...(item?.annotations || []), res.annotation]
          }
      )
    } else if (res.annotation) {
      const krpano = (document.getElementById('embedpano-full') ||
        document.getElementById('embedpano-viewer')) as any
      if (krpano) {
        Krpano.AddAnnotation(
          krpano,
          [res.annotation],
          null,
          false,
          true,
          'rn',
          this.deps.userList,
          this.deps.currentUser
        )
      }

      this.deps.setPointDetail(
        (item: any) =>
          item && {
            ...item,
            ...(newVersion != null ? { pointVersionId: newVersion } : {}),
            annotations: (item?.annotations || []).map((anno: any) =>
              anno.id === res.annotation.id ? res.annotation : anno
            )
          }
      )
    }

    const mentionedUserIds = CommentFormService.resolveMentionedUserIds(
      payload?.mentions || [],
      this.deps.userList || [],
      this.deps.userGroups || [],
      this.deps.currentUser?.id
    )

    if (mentionedUserIds.length > 0) {
      notification.success({
        message: this.deps.t(
          'comment.mention_notified',
          'メンション {{count}}名 に通知',
          { count: mentionedUserIds.length }
        )
      })
    } else {
      notification.success({
        message: this.deps.t('building.saved', '保存しました')
      })
    }
    this.deps.setCurrentSelected(undefined)
  }

  /**
   * Executes posting a reply to a comment thread without closing the modal
   */
  async handleReply(commentId: number | string, payload: any): Promise<void> {
    const textToCheck =
      typeof payload === 'string' ? payload : payload?.body || ''
    if (!textToCheck.trim()) return

    const newReply = await this.deps.addReply(commentId, payload)

    // Update locally in currentSelected so reply displays instantly
    this.deps.setCurrentSelected((prev: any) => {
      if (!prev) return prev
      const embedded =
        prev.comment_object || prev.commentObject || prev.comment || {}
      const existingReplies = embedded.replies || prev.replies || []
      const updatedReplies = [...existingReplies, newReply]
      return {
        ...prev,
        replies: updatedReplies,
        comment_object: {
          ...embedded,
          replies: updatedReplies
        }
      }
    })

    const mentionedUserIds = CommentFormService.resolveMentionedUserIds(
      payload?.mentions || [],
      this.deps.userList || [],
      this.deps.userGroups || [],
      this.deps.currentUser?.id
    )

    if (mentionedUserIds.length > 0) {
      notification.success({
        message: this.deps.t(
          'comment.mention_notified',
          'メンション {{count}}名 に通知',
          { count: mentionedUserIds.length }
        )
      })
    } else {
      notification.success({
        message: this.deps.t('comment.reply_sent', '返信を送信しました')
      })
    }
  }

  /**
   * Executes delete comment workflow & Krpano hotspot removal
   */
  async handleDelete(
    commentId: number,
    currentSelected: any,
    buildingId?: number | string,
    floorId?: number | string,
    pointId?: string,
    pointDetail?: any,
    points?: any[]
  ): Promise<void> {
    await this.deps.deleteComment(commentId)

    const annotationId = currentSelected?.id
    if (annotationId && this.deps.deleteAnnotation) {
      try {
        const targetBuildingId =
          buildingId ||
          currentSelected?.building_id ||
          currentSelected?.buildingId
        const targetFloorId =
          floorId || currentSelected?.floor_id || currentSelected?.floorId
        const targetPointId =
          pointId || currentSelected?.point_id || currentSelected?.pointId

        const versionIdToSend = getPointVersion(
          targetPointId,
          pointDetail,
          points
        )

        const deleteRes = await this.deps.deleteAnnotation({
          method: 'delete',
          query: [annotationId],
          headers: { 'X-Point-Version-ID': String(versionIdToSend) },
          params: {
            building_id: Number(targetBuildingId),
            floor_id: Number(targetFloorId),
            point_id: targetPointId
          },
          onError: (error: any) => handlePointVersionError(error, this.deps.t)
        })

        const newVersion = getPointVersionFromResponse(deleteRes)
        if (newVersion != null && this.deps.setPoints) {
          syncPointVersion(
            newVersion,
            targetPointId,
            this.deps.setPointDetail,
            this.deps.setPoints
          )
        }

        const krpano = (document.getElementById('embedpano-full') ||
          document.getElementById('embedpano-viewer')) as any
        if (krpano) {
          krpano.call?.(`remove_annotation(${annotationId})`)
          krpano.call?.(`removehotspot(id-${annotationId})`)
          krpano.call?.(`removehotspot(head-${annotationId})`)
          krpano.call?.(`removehotspot(middle-${annotationId})`)
          krpano.call?.(`removehotspot(polyline-${annotationId})`)
          krpano.call?.(`removehotspot(polyline2-${annotationId})`)
          krpano.call?.(`removehotspot(year-${annotationId})`)
        }

        this.deps.setPointDetail(
          (item: any) =>
            item && {
              ...item,
              ...(newVersion != null ? { pointVersionId: newVersion } : {}),
              annotations: (item?.annotations || []).filter(
                (a: any) => a.id !== annotationId
              )
            }
        )
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete annotation:', err)
      }
    }

    notification.success({
      message: this.deps.t('building.deleted', '削除しました')
    })
    this.deps.setCurrentSelected(undefined)
  }

  /**
   * Cancels annotation editing
   */
  handleCancel(currentSelected: any): void {
    const krpano = (document.getElementById('embedpano-full') ||
      document.getElementById('embedpano-viewer')) as any
    if (krpano) {
      krpano.call?.('hide_temp_hotspot()')
    }
    if (!currentSelected?.id) {
      Krpano.RemoveTempAnnotation()
    }
    this.deps.setCurrentSelected(undefined)
  }

  /**
   * Filters out recipient IDs of users no longer in the active userList
   */
  static filterValidRecipients(recipients: any[], userList?: any[]): any[] {
    const base = Array.isArray(recipients) ? recipients : []
    const ensured = CommentFormService.ensureAdminRecipients(base, userList)
    if (!Array.isArray(userList) || userList.length === 0) return ensured

    const validUserIds = new Set(
      userList
        .map((u: any) => Number(u?.id || u?.user_id))
        .filter((id) => !Number.isNaN(id) && id > 0)
    )

    return ensured.filter((r: any) => {
      const rId =
        typeof r === 'number'
          ? r
          : Number(r?.recipient_id || r?.recipientId || r?.id || r)
      return validUserIds.has(rId)
    })
  }

  /**
   * Checks whether the valid recipients list matches all active building users
   */
  static checkIsAllRecipients(
    validRecipients: any[],
    userList?: any[]
  ): boolean {
    return CommentEntity.from({ recipients: validRecipients }).isAllRecipients(
      userList
    )
  }

  /**
   * Returns recipient display metadata from CommentEntity
   */
  static getRecipientDisplayInfo(rawRecipients: any[], userList?: any[]) {
    return CommentEntity.from({
      recipients: rawRecipients
    }).getRecipientDisplayInfo(userList)
  }

  /**
   * Evaluates if current user has permission to view the comment
   */
  static canUserViewComment(
    annotation: any,
    currentUser?: { id?: number | string; role?: string } | null,
    userList?: any[]
  ): boolean {
    return CommentEntity.from(annotation).canUserView(currentUser, userList)
  }

  /**
   * Extracts hotspot marker metadata for KRpano Comment hotspots
   */
  static extractCommentMarkerInfo(annotation: any, userList?: any[]) {
    const commentObj =
      annotation?.comment_object ||
      annotation?.commentObject ||
      annotation?.comment

    const isResolved = (commentObj?.status || annotation?.status) === 'resolved'
    const objectCat =
      commentObj?.object_cat ||
      annotation?.comment_object?.object_cat ||
      annotation?.comment?.object_cat ||
      annotation?.object_cat ||
      ''
    const isLocked = Boolean(annotation?.locked || annotation?.isLocked)
    const rawTitle =
      commentObj?.title ||
      annotation?.title ||
      annotation?.name ||
      annotation?.set_annotation_name ||
      'コメント'
    const titleText = decodeHtmlEntities(rawTitle)
    const commentEntity = CommentEntity.from(annotation)
    const recipientInfo = commentEntity.getRecipientDisplayInfo(userList)
    const rawScope = commentObj?.scope || annotation?.scope
    const scope = rawScope || (commentObj ? 'common' : '')

    let authorName =
      commentObj?.creator_name ||
      commentObj?.creatorName ||
      commentObj?.author?.name ||
      annotation?.creator_name ||
      annotation?.creatorName ||
      ''

    if (!authorName) {
      const userObj =
        annotation?.user || annotation?.author || annotation?.created_by_user
      if (userObj) {
        if (typeof userObj === 'string') {
          authorName = userObj
        } else if (userObj.name) {
          authorName = userObj.name
        } else if (userObj.last_name || userObj.first_name) {
          authorName = `${userObj.last_name || ''} ${
            userObj.first_name || ''
          }`.trim()
        }
      }
    }

    const year = commentObj?.year || annotation?.year || ''

    return {
      isResolved,
      objectCat,
      isLocked,
      titleText,
      recipientCount: recipientInfo.recipientCount,
      isAllRecipients: recipientInfo.isAllRecipients,
      recipientInfo,
      scope,
      authorName,
      year
    }
  }

  /**
   * Resolves category color from ANNOTATION_CATEGORIES master list
   */
  static getCategoryColor(cat?: string): string {
    const key = (cat || '').toUpperCase()
    const found = ANNOTATION_CATEGORIES.find((c) => c.key === key)
    const fallback =
      ANNOTATION_CATEGORIES.find((c) => c.key === 'A') ||
      ANNOTATION_CATEGORIES[0]
    return found?.colorHex || fallback?.colorHex || ''
  }

  static panKrpanoToCommentMarker(annotationId: string | number) {
    if (!annotationId) return

    const krpano =
      new KrpanoEngine('full').instance || new KrpanoEngine('viewer').instance
    if (!krpano) return

    const hotspotName = `id-${annotationId}`
    const ath = krpano.get(`hotspot[${hotspotName}].ath`)
    const atv = krpano.get(`hotspot[${hotspotName}].atv`)

    if (ath !== null && atv !== null && ath !== undefined) {
      const screenWidth =
        typeof window !== 'undefined' ? window.innerWidth || 1024 : 1024
      const modalWidth = Math.max(screenWidth * 0.5, 670)
      const modalLeftEdge = Math.max(0, screenWidth - 60 - modalWidth) // 60 is the right margin
      const visibleCenter = modalLeftEdge / 2
      const shiftRatio = (screenWidth / 2 - visibleCenter) / screenWidth

      const hfov = Number(
        krpano.get('view.hfov') || krpano.get('view.fov') || 90
      )
      const targetHlookat = Number(ath) + hfov * shiftRatio

      krpano.call(`lookto(${targetHlookat}, ${atv}, ${krpano.get('view.fov')})`)
    }
  }

  /**
   * Triggers the existing annotation reload mechanism to update 360 comment markers
   */
  static triggerAnnotationRefresh(
    pointId: string | undefined,
    setIsRefetchingAnnotations: (val: boolean) => void,
    setRevalidatePointDetail: (val: string | undefined) => void
  ) {
    if (!pointId) return
    setIsRefetchingAnnotations(true)
    setRevalidatePointDetail(pointId)
  }

  /**
   * Resolves the target annotation and camera coordinates (h, v) for a given comment
   */
  static async resolveCommentTargetPosition(
    commentItem: CommentItem,
    points?: any[]
  ): Promise<ResolvedCommentTargetPosition> {
    const aId = String(
      commentItem.annotationId || (commentItem as any).annotation_id || ''
    )
    const bId = commentItem.buildingId || (commentItem as any).building_id
    const fId = commentItem.floorId || (commentItem as any).floor_id
    const pId = String(
      commentItem.pointId || (commentItem as any).point_id || ''
    )

    let targetAnno: any = null
    let h: number | undefined =
      commentItem.posH ??
      (commentItem as any).pos_h ??
      (commentItem as any).h ??
      (commentItem as any).head_h
    let v: number | undefined =
      commentItem.posV ??
      (commentItem as any).pos_v ??
      (commentItem as any).v ??
      (commentItem as any).head_v

    if (points && Array.isArray(points)) {
      points.forEach((pt) => {
        if (targetAnno || !pt.annotations) return
        const annos = Array.isArray(pt.annotations)
          ? pt.annotations
          : Object.values(pt.annotations)
        const found = annos.find((a: any) => {
          const cId =
            a?.comment_object?.id ||
            a?.comment_object?.comment_id ||
            a?.commentObject?.id ||
            a?.commentObject?.comment_id ||
            a?.comment?.id ||
            a?.comment_id ||
            a?.commentId
          return (
            (cId && String(cId) === String(commentItem.id)) ||
            (aId && String(a?.id) === aId)
          )
        })
        if (found) {
          targetAnno = found
        }
      })
    }

    if (targetAnno) {
      h =
        targetAnno.h ??
        targetAnno.head_h ??
        targetAnno.pos_h ??
        (targetAnno as any).posH
      v =
        targetAnno.v ??
        targetAnno.head_v ??
        targetAnno.pos_v ??
        (targetAnno as any).posV
    }

    if ((h === undefined || v === undefined || (h === 0 && v === 0)) && aId) {
      try {
        const res = await Api.get<any>(`v1/annotations/${aId}`, {
          building_id: bId,
          ...(fId ? { floor_id: fId } : {}),
          ...(pId ? { point_id: pId } : {})
        })
        if (res) {
          targetAnno = targetAnno || res
          h = res.h ?? res.head_h ?? res.pos_h ?? res.posH
          v = res.v ?? res.head_v ?? res.pos_v ?? res.posV
        }
      } catch {
        // ignore annotation detail lookup failure
      }
    }

    const annotationId = targetAnno?.id || aId || String(commentItem.id)

    return {
      annotationId,
      targetAnno,
      h,
      v
    }
  }
}

export default CommentDomainService
